/**
 * Plan, step 1 — Sort my tasks: give every open task a "when" (pickup day, this or next week, later, someday) or close
 * it. One by one with keyboard shortcuts, or as a list with bulk moves. The deadline is never changed here.
 */
import { CategoryPick } from "../components/plan/CategoryPick";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import {
  Ban, CalendarClock, CalendarDays, CalendarRange, Check, ChevronRight, Clock, CornerDownRight, Layers, List, Moon, PartyPopper, RotateCcw, Search, SkipForward, Sun, Sunrise, X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, patch, post } from "../lib/api";
import { Card, Empty, ErrorNote, PageHeader } from "../components/ui";
import { PlanSteps } from "../components/plan/PlanSteps";

type View = "undecided" | "week" | "nextweek" | "later" | "someday" | "all";
type Action = "today" | "tomorrow" | "week" | "nextweek" | "date" | "later" | "someday" | "done" | "cancel";
type Horizon = "next" | "later" | "someday" | null;
interface SortItem {
  id: string; ref: string; title: string; priority: string; dueAt: string | null; plannedDate: string | null; focusWeek: string | null; horizon: Horizon;
  estimateMinutes: number | null; decisionNeeded: boolean; personal: boolean; statusKey: string; company: string | null; category: { id: string; name: string; color: string } | null; createdAt: string;
  parent: { id: string; ref: string; title: string } | null;
}
interface SortData { today: string; week: string; nextWeek: string; counts: Record<View, number>; items: SortItem[] }
interface When { plannedDate: string | null; focusWeek: string | null; horizon: Horizon }
type Override = When | "closed";
interface UndoEntry { id: string; prev: When & { estimateMinutes: number | null }; sentEstimate: boolean }
interface Toast { key: number; text: string; undo: UndoEntry[] | null; closedId?: string }

const VIEWS: Array<{ view: View; label: string }> = [
  { view: "undecided", label: "To sort" },
  { view: "week", label: "This week" },
  { view: "nextweek", label: "Next week" },
  { view: "later", label: "Later" },
  { view: "someday", label: "Someday" },
  { view: "all", label: "All" },
];
const COMPANIES = ["IFG", "IFT", "IFS", "IFI", "IFP", "MYT"];
const MINUTES = [15, 30, 60, 90, 120];
const DEFAULT_MIN = 30;
const LIMIT = 200;

const BUTTONS: Array<{ action: Action | "skip"; label: string; key: string; icon: React.ReactNode; tone?: string }> = [
  { action: "today", label: "Today", key: "1", icon: <Sun size={16} /> },
  { action: "tomorrow", label: "Tomorrow", key: "2", icon: <Sunrise size={16} /> },
  { action: "week", label: "This week", key: "3", icon: <CalendarDays size={16} /> },
  { action: "nextweek", label: "Next week", key: "4", icon: <CalendarRange size={16} /> },
  { action: "date", label: "Pick a date", key: "5", icon: <CalendarClock size={16} /> },
  { action: "later", label: "Later", key: "6", icon: <Clock size={16} /> },
  { action: "someday", label: "Someday", key: "7", icon: <Moon size={16} /> },
  { action: "done", label: "Done", key: "d", icon: <Check size={16} />, tone: "text-emerald-700" },
  { action: "cancel", label: "Drop", key: "x", icon: <Ban size={16} />, tone: "text-red-700" },
  { action: "skip", label: "Skip", key: "s", icon: <SkipForward size={16} /> },
];
const BULK: Array<{ action: Action; label: string }> = [
  { action: "today", label: "Today" }, { action: "tomorrow", label: "Tomorrow" }, { action: "week", label: "This week" },
  { action: "nextweek", label: "Next week" }, { action: "later", label: "Later" }, { action: "someday", label: "Someday" },
];
const ACTION_TEXT: Record<Action, string> = {
  today: "today", tomorrow: "tomorrow", week: "this week", nextweek: "next week", date: "", later: "Later", someday: "Someday", done: "done", cancel: "dropped",
};

// date helpers (YYYY-MM-DD, same rules as the server)
const day = (v: string | null | undefined) => (v ? v.slice(0, 10) : null);
const addDays = (d: string, n: number) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
const mondayOf = (d: string) => addDays(d, -((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7));
const nextWorkday = (d: string) => { let x = addDays(d, 1); while ([0, 6].includes(new Date(`${x}T12:00:00Z`).getUTCDay())) x = addDays(x, 1); return x; };
const fmtDay = (d: string) => new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const fmtDue = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Malta" });

/** What the server will set, so the page can move on before it answers. */
function predict(a: Action, d: SortData, date?: string): When {
  if (a === "today") return { plannedDate: d.today, focusWeek: mondayOf(d.today), horizon: null };
  if (a === "tomorrow") { const t = nextWorkday(d.today); return { plannedDate: t, focusWeek: mondayOf(t), horizon: null }; }
  if (a === "date" && date) return { plannedDate: date, focusWeek: mondayOf(date), horizon: null };
  if (a === "week") return { plannedDate: null, focusWeek: d.week, horizon: null };
  if (a === "nextweek") return { plannedDate: null, focusWeek: d.nextWeek, horizon: null };
  return { plannedDate: null, focusWeek: null, horizon: a === "later" || a === "someday" ? a : null };
}

function matches(view: View, w: When, d: SortData) {
  const pd = day(w.plannedDate), fw = day(w.focusWeek);
  if (view === "undecided") return !pd && !fw && !w.horizon;
  if (view === "week") return fw === d.week && !pd;
  if (view === "nextweek") return fw === d.nextWeek && !pd;
  if (view === "later" || view === "someday") return w.horizon === view && !pd;
  return true;
}

function whenLabel(w: When, d: SortData) {
  const pd = day(w.plannedDate), fw = day(w.focusWeek);
  if (pd) return pd === d.today ? "Today" : `Pickup ${fmtDay(pd)}`;
  if (fw === d.week) return "This week";
  if (fw === d.nextWeek) return "Next week";
  if (fw) return `Week of ${fmtDay(fw)}`;
  if (w.horizon === "later") return "Later";
  if (w.horizon === "someday") return "Someday";
  return "To sort";
}

const readMode = (): "one" | "list" => { try { return localStorage.getItem("plan-sort-mode") === "list" ? "list" : "one"; } catch { return "one"; } };
const saveMode = (m: "one" | "list") => { try { localStorage.setItem("plan-sort-mode", m); } catch { /* private mode */ } };

export function PlanSortPage() {
  const qc = useQueryClient();
  const [view, setView] = useState<View>("undecided");
  const [company, setCompany] = useState("");
  const [search, setSearch] = useState("");
  const [qText, setQText] = useState("");
  const [mode, setModeState] = useState<"one" | "list">(readMode);
  const [overrides, setOverrides] = useState<Map<string, Override>>(new Map());
  const [seen, setSeen] = useState<Set<string>>(new Set()); // one-by-one: decided or skipped this visit
  const [tally, setTally] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQText(search.trim()), 250); return () => clearTimeout(t); }, [search]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 5000); return () => clearTimeout(t); }, [toast]);
  useEffect(() => { setSeen(new Set()); setSelected(new Set()); setTally(0); }, [view, company, qText]);

  const params = new URLSearchParams({ view, limit: String(LIMIT), ...(company ? { company } : {}), ...(qText ? { q: qText } : {}) });
  const q = useQuery({ queryKey: ["plan-sort", view, company, qText], queryFn: () => api<SortData>(`/api/plan/sort?${params}`) });
  const d = q.data;

  const setMode = (m: "one" | "list") => { setModeState(m); saveMode(m); };
  const whenOf = (i: SortItem): When => { const o = overrides.get(i.id); return o && o !== "closed" ? o : { plannedDate: i.plannedDate, focusWeek: i.focusWeek, horizon: i.horizon }; };

  const visible = useMemo(() => (d ? d.items.filter((i) => { const o = overrides.get(i.id); return o !== "closed" && matches(view, o ?? i, d); }) : []), [d, overrides, view]);
  const queue = visible.filter((i) => !seen.has(i.id));
  const current = queue[0] ?? null;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["plan-sort"] });
    void qc.invalidateQueries({ queryKey: ["plan-sort-count"] });
    void qc.invalidateQueries({ queryKey: ["plan-day"] });
    void qc.invalidateQueries({ queryKey: ["plan-today"] });
    void qc.invalidateQueries({ queryKey: ["my-day"] });
  };
  const setOv = (id: string, v: Override | null) => setOverrides((m) => { const n = new Map(m); if (v) n.set(id, v); else n.delete(id); return n; });

  /** Decide one or more tasks: move on at once, tell the server, put it back if the server says no. */
  const decide = async (items: SortItem[], a: Action, opts: { date?: string; estimate?: number } = {}) => {
    if (!d || !items.length) return;
    setError(null);
    const entries: UndoEntry[] = items.map((i) => ({ id: i.id, prev: { ...whenOf(i), estimateMinutes: i.estimateMinutes }, sentEstimate: opts.estimate !== undefined }));
    const closing = a === "done" || a === "cancel";
    for (const i of items) setOv(i.id, closing ? "closed" : predict(a, d, opts.date));
    setSeen((s) => { const n = new Set(s); items.forEach((i) => n.add(i.id)); return n; });
    setTally((n) => n + items.length);
    const label = items.length === 1 ? `"${items[0]!.title}"` : `${items.length} tasks`;
    const where = closing ? ACTION_TEXT[a] : `to ${a === "date" && opts.date ? fmtDay(opts.date) : ACTION_TEXT[a]}`;
    setToast({ key: Date.now(), text: `${closing ? "Marked" : "Moved"} ${label} ${where}`, undo: closing ? null : entries, closedId: closing && items.length === 1 ? items[0]!.id : undefined });

    const results = await Promise.allSettled(items.map((i) => post<When & { ok: boolean }>(`/api/plan/sort/${i.id}`, {
      action: a, ...(a === "date" ? { date: opts.date } : {}), ...(opts.estimate !== undefined && !closing ? { estimateMinutes: opts.estimate } : {}),
    })));
    let failed = 0;
    results.forEach((r, k) => {
      const id = items[k]!.id;
      if (r.status === "fulfilled") { if (!closing) setOv(id, { plannedDate: r.value.plannedDate, focusWeek: r.value.focusWeek, horizon: r.value.horizon }); }
      else { failed++; setOv(id, null); setSeen((s) => { const n = new Set(s); n.delete(id); return n; }); setError(r.reason); }
    });
    if (failed) { setTally((n) => n - failed); setToast(null); }
    refresh();
  };

  const undo = async (t: Toast) => {
    if (!t.undo) return;
    setToast(null);
    setError(null);
    const results = await Promise.allSettled(t.undo.map((u) => patch(`/api/work/items/${u.id}`, {
      plannedDate: day(u.prev.plannedDate), focusWeek: day(u.prev.focusWeek), horizon: u.prev.horizon, ...(u.sentEstimate ? { estimateMinutes: u.prev.estimateMinutes } : {}),
    })));
    results.forEach((r, k) => {
      const u = t.undo![k]!;
      if (r.status === "fulfilled") {
        setOv(u.id, { plannedDate: u.prev.plannedDate, focusWeek: u.prev.focusWeek, horizon: u.prev.horizon });
        setSeen((s) => { const n = new Set(s); n.delete(u.id); return n; });
        setTally((n) => Math.max(0, n - 1));
      } else setError(r.reason);
    });
    refresh();
  };

  const counts = d?.counts;
  const more = d && d.items.length >= LIMIT;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PlanSteps current="sort" left={counts?.undecided} />
      <PageHeader
        eyebrow="Plan · step 1"
        title="Sort my tasks"
        subtitle="Give every task a pickup day, a week, or park it. The deadline stays as it is."
        actions={
          <div className="tabs" role="group" aria-label="How to sort">
            <button className={clsx("tab", mode === "one" && "active")} aria-pressed={mode === "one"} onClick={() => setMode("one")}><Layers size={14} /> One by one</button>
            <button className={clsx("tab", mode === "list" && "active")} aria-pressed={mode === "list"} onClick={() => setMode("list")}><List size={14} /> List</button>
          </div>
        }
      />

      <div className="space-y-3">
        <div className="max-w-full overflow-x-auto">
          <div className="tabs" role="tablist" aria-label="Which tasks">
            {VIEWS.map((v) => (
              <button key={v.view} role="tab" aria-selected={view === v.view} className="tab" onClick={() => setView(v.view)}>
                {v.label}{counts && <span className="count num">{counts[v.view]}</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className="input !w-auto" value={company} onChange={(e) => setCompany(e.target.value)} aria-label="Company">
            <option value="">All companies</option>
            {COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="relative min-w-0 flex-1 sm:max-w-xs">
            <span className="sr-only">Search tasks</span>
            <Search size={15} className="muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="input !pl-9" type="search" placeholder="Search titles" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
        </div>
      </div>

      <ErrorNote error={q.error ?? error} />

      {q.isLoading || !d ? (
        <Empty>{q.isLoading ? "Loading your tasks…" : "Couldn't load your tasks."}</Empty>
      ) : mode === "one" ? (
        <OneByOne
          key={current?.id ?? "none"}
          d={d}
          view={view}
          current={current}
          left={queue.length}
          more={!!more}
          tally={tally}
          skippedLeft={visible.length - queue.length}
          filtered={!!(company || qText)}
          whenOf={whenOf}
          onDecide={(i, a, o) => void decide([i], a, o)}
          onSkip={(i) => setSeen((s) => new Set(s).add(i.id))}
          onReset={() => setSeen(new Set())}
        />
      ) : (
        <ListMode
          d={d}
          view={view}
          items={visible}
          more={!!more}
          selected={selected}
          setSelected={setSelected}
          busy={bulkBusy}
          whenOf={whenOf}
          onDecide={(i, a, date) => void decide([i], a, { date })}
          onBulk={async (a) => {
            setBulkBusy(true);
            try { await decide(visible.filter((i) => selected.has(i.id)), a); setSelected(new Set()); } finally { setBulkBusy(false); }
          }}
        />
      )}

      {toast && (
        <div role="status" className="fixed bottom-20 left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-xl bg-[var(--text)] px-4 py-2.5 text-sm text-[var(--bg)] shadow-lg md:bottom-6">
          <span className="min-w-0 truncate">{toast.text}</span>
          {toast.undo ? (
            <button className="inline-flex shrink-0 items-center gap-1 font-semibold underline-offset-2 hover:underline" onClick={() => void undo(toast)}><RotateCcw size={14} /> Undo</button>
          ) : toast.closedId ? (
            <Link to="/work/$id" params={{ id: toast.closedId }} target="_blank" rel="noopener" className="shrink-0 text-xs underline-offset-2 opacity-80 hover:underline">Reopen it from the task</Link>
          ) : (
            <span className="shrink-0 text-xs opacity-80">Reopen from the task</span>
          )}
          <button aria-label="Close" className="shrink-0 opacity-70 hover:opacity-100" onClick={() => setToast(null)}><X size={14} /></button>
        </div>
      )}
    </div>
  );
}

function Meta({ i, d, when }: { i: SortItem; d: SortData; when?: string }) {
  const overdue = i.dueAt && i.dueAt.slice(0, 10) < d.today;
  return (
    <span className="muted flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {i.parent && (
        <Link to="/work/$id" params={{ id: i.parent.id }} target="_blank" rel="noopener" className="flex basis-full items-center gap-1 hover:underline" title={`Subtask of ${i.parent.ref}`}>
          <CornerDownRight size={12} className="shrink-0" /> Subtask of <span className="font-medium text-[var(--text)]">{i.parent.title}</span>
        </Link>
      )}
      <span className="num">{i.ref}</span>
      {i.company && <span className="chip">{i.company}</span>}
      <CategoryPick key={i.id} itemId={i.id} value={i.category} />
      {i.priority === "P1" ? <span className="chip text-red-700">Urgent</span> : i.priority === "P2" ? <span className="chip">High</span> : null}
      {i.decisionNeeded && <span className="chip text-amber-700">Decision</span>}
      {i.dueAt && <span className={clsx(overdue && "font-semibold text-red-700")}>Due {fmtDue(i.dueAt)}</span>}
      {when && <span className="chip">{when}</span>}
    </span>
  );
}

function OneByOne({ d, view, current, left, more, tally, skippedLeft, filtered, whenOf, onDecide, onSkip, onReset }: {
  d: SortData; view: View; current: SortItem | null; left: number; more: boolean; tally: number; skippedLeft: number; filtered: boolean;
  whenOf: (i: SortItem) => When;
  onDecide: (i: SortItem, a: Action, o: { date?: string; estimate?: number }) => void;
  onSkip: (i: SortItem) => void;
  onReset: () => void;
}) {
  const [est, setEst] = useState<number>(current?.estimateMinutes ?? DEFAULT_MIN);
  const [estChanged, setEstChanged] = useState(false);
  const [custom, setCustom] = useState("");
  const [picking, setPicking] = useState(false);
  const [date, setDate] = useState("");
  const dateRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (picking) dateRef.current?.focus(); }, [picking]);

  const chooseEst = (m: number) => { setEst(m); setEstChanged(true); };
  const act = (a: Action | "skip") => {
    if (!current) return;
    if (a === "skip") return onSkip(current);
    if (a === "date") {
      if (!picking || !date) { setPicking(true); return; }
      return onDecide(current, "date", { date, estimate: estChanged ? est : undefined });
    }
    onDecide(current, a, { estimate: estChanged ? est : undefined });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement || (t instanceof HTMLElement && t.isContentEditable) || e.metaKey || e.ctrlKey || e.altKey) return;
      const b = BUTTONS.find((x) => x.key === e.key.toLowerCase());
      if (b) { e.preventDefault(); act(b.action); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!current) {
    if (skippedLeft) {
      return <Card><Empty icon={<SkipForward size={20} />} action={<button className="btn btn-sm" onClick={onReset}>Go through them again</button>}>You skipped the remaining {skippedLeft}.</Empty></Card>;
    }
    return <AllSorted view={view} tally={tally} filtered={filtered} />;
  }

  const total = tally + left;
  const pct = total ? Math.round((tally / total) * 100) : 0;
  return (
    <Card>
      <div className="space-y-4">
        <div>
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="font-semibold num">{left}{more ? "+" : ""} left</span>
            {tally > 0 && <span className="muted num">{tally} sorted</span>}
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--panel-2)] ring-1 ring-[var(--line)]" role="progressbar" aria-label="Sorted so far" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className="h-full rounded-full bg-brand-600 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div>
          <Meta i={current} d={d} when={view === "all" ? whenLabel(whenOf(current), d) : undefined} />
          <Link to="/work/$id" params={{ id: current.id }} target="_blank" rel="noopener" className="mt-2 block text-lg font-semibold leading-snug hover:underline">{current.title}</Link>
          <p className="muted mt-1 text-xs">Added {fmtDay(current.createdAt)}</p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="How long it takes">
          <span className="muted mr-1 text-sm">Takes about</span>
          {MINUTES.map((m) => (
            <button key={m} className={clsx("chip cursor-pointer", est === m && !custom && "!bg-brand-50 !text-brand-700 ring-1 ring-brand-200")} aria-pressed={est === m && !custom} onClick={() => { setCustom(""); chooseEst(m); }}>{m}</button>
          ))}
          <input
            type="number" min={5} max={600} step={5} inputMode="numeric" placeholder="other" aria-label="Other minutes"
            className="input !h-7 !min-h-0 !w-20 !py-0 text-sm" value={custom}
            onChange={(e) => { setCustom(e.target.value); const n = Number(e.target.value); if (n >= 5 && n <= 600) chooseEst(Math.round(n)); }}
          />
          <span className="muted text-sm">min</span>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {BUTTONS.map((b) => (
            <button key={b.action} className={clsx("btn justify-start", b.tone, b.action === "date" && picking && "ring-1 ring-brand-300")} onClick={() => act(b.action)} aria-keyshortcuts={b.key.toUpperCase()}>
              {b.icon} {b.label} <kbd className="muted ml-auto text-[11px]">{b.key.toUpperCase()}</kbd>
            </button>
          ))}
        </div>
        {picking && (
          <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (date) act("date"); }}>
            <CalendarClock size={16} className="muted" />
            <input ref={dateRef} type="date" className="input !w-44" min={d.today} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Pickup day" />
            <button type="submit" className="btn btn-primary" disabled={!date}>Set pickup day</button>
            <button type="button" className="btn btn-ghost" onClick={() => { setPicking(false); setDate(""); }}>Cancel</button>
          </form>
        )}
      </div>
    </Card>
  );
}

function AllSorted({ view, tally, filtered }: { view: View; tally: number; filtered: boolean }) {
  if (view !== "undecided") return <Card><Empty icon={<Layers size={20} />}>{filtered ? "Nothing matches." : "Nothing here."}</Empty></Card>;
  return (
    <Card>
      <Empty
        icon={<PartyPopper size={20} />}
        action={!filtered && <Link to="/plan/week" className="btn btn-primary mt-2">Next: Plan my week <ChevronRight size={15} /></Link>}
      >
        {filtered ? "Nothing left to sort here. Clear the filter to see the rest." : tally ? `All sorted. ${tally} decided just now.` : "Every task has a when. Nothing to sort."}
      </Empty>
    </Card>
  );
}

const ROW_ACTIONS: Array<{ action: Action; label: string; short: string }> = [
  { action: "today", label: "Today", short: "Today" },
  { action: "tomorrow", label: "Tomorrow", short: "Tmrw" },
  { action: "week", label: "This week", short: "Week" },
  { action: "nextweek", label: "Next week", short: "Next wk" },
  { action: "later", label: "Later", short: "Later" },
  { action: "someday", label: "Someday", short: "Someday" },
];

function ListMode({ d, view, items, more, selected, setSelected, busy, whenOf, onDecide, onBulk }: {
  d: SortData; view: View; items: SortItem[]; more: boolean; selected: Set<string>; setSelected: (s: Set<string>) => void; busy: boolean;
  whenOf: (i: SortItem) => When;
  onDecide: (i: SortItem, a: Action, date?: string) => void;
  onBulk: (a: Action) => Promise<void>;
}) {
  if (!items.length) return <AllSorted view={view} tally={0} filtered={false} />;
  const n = [...selected].filter((id) => items.some((i) => i.id === id)).length;
  const all = n === items.length;
  const toggle = (id: string) => { const s = new Set(selected); if (s.has(id)) s.delete(id); else s.add(id); setSelected(s); };
  return (
    <>
      {n > 0 && (
        <div className="panel sticky top-2 z-10 flex flex-wrap items-center gap-2 px-3 py-2.5" role="region" aria-label="Move selected tasks">
          <span className="text-sm font-semibold">Move {n} to:</span>
          {BULK.map((b) => <button key={b.action} className="btn btn-sm" disabled={busy} onClick={() => void onBulk(b.action)}>{b.label}</button>)}
          <button className="btn btn-sm btn-ghost ml-auto" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}
      <Card flush>
        <div className="flex items-center gap-3 border-b border-[var(--line)] px-4 py-2.5 text-xs">
          <input type="checkbox" className="size-4" checked={all} onChange={() => setSelected(all ? new Set() : new Set(items.map((i) => i.id)))} aria-label="Select all" />
          <span className="muted num">{items.length}{more ? "+" : ""} tasks</span>
        </div>
        <ul>
          {items.map((i) => (
            <li key={i.id} className="flex flex-col gap-2 border-b border-[var(--line)] px-4 py-3 last:border-b-0 sm:flex-row sm:items-start">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <input type="checkbox" className="mt-1 size-4 shrink-0" checked={selected.has(i.id)} onChange={() => toggle(i.id)} aria-label={`Select ${i.title}`} />
                <div className="min-w-0 flex-1">
                  <Link to="/work/$id" params={{ id: i.id }} target="_blank" rel="noopener" className="block text-sm font-medium hover:underline">{i.title}</Link>
                  <div className="mt-1"><Meta i={i} d={d} when={view === "all" ? whenLabel(whenOf(i), d) : undefined} /></div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1 pl-7 sm:max-w-[19rem] sm:justify-end sm:pl-0" role="group" aria-label={`Decide ${i.title}`}>
                {ROW_ACTIONS.map((r) => (
                  <button key={r.action} className="btn btn-sm !px-2" title={r.label} aria-label={`${r.label}: ${i.title}`} onClick={() => onDecide(i, r.action)}>{r.short}</button>
                ))}
                <input
                  type="date" className="input !h-8 !min-h-0 !w-[8.5rem] !py-0 text-xs" min={d.today} value="" aria-label={`Pick a pickup day for ${i.title}`}
                  onChange={(e) => { if (e.target.value) onDecide(i, "date", e.target.value); }}
                />
                <button className="btn btn-sm btn-icon !px-2 text-emerald-700" title="Done" aria-label={`Done: ${i.title}`} onClick={() => onDecide(i, "done")}><Check size={14} /></button>
                <button className="btn btn-sm btn-icon !px-2 text-red-700" title="Drop" aria-label={`Drop: ${i.title}`} onClick={() => onDecide(i, "cancel")}><Ban size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
